import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import MedicalProviderFormDialog from "@/components/MedicalProviderFormDialog";
import MedicalProviderList from "@/components/MedicalProviderList";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Stethoscope, Plus } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ClientMedicalProviders({ clientId }) {
  const [providers, setProviders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/medical-providers`).then((r) => { setProviders(r.data.providers); setSummary(r.data.summary); }).catch(() => {});
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/medical-providers/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const confirmDelete = async () => {
    try { await api.delete(`/medical-providers/${deleteTarget.id}`); toast.success("Provider deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="client-medical-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
          <Stethoscope className="w-4 h-4 text-gray-400" /> Medical Providers
        </h3>
        <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="client-add-provider-button"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:text-[#002480] border border-gray-200 hover:border-[#002FA7] px-2 py-1 rounded-sm transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add provider
        </button>
      </div>

      {summary && summary.count > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="client-medical-summary">
          <div className="bg-[#002FA7]/5 rounded-sm p-3">
            <p className="font-heading text-lg font-extrabold text-[#002FA7]" data-testid="mp-total-billed">{fmt(summary.total_billed)}</p>
            <p className="text-xs font-semibold text-gray-500">Total billed</p>
          </div>
          <div className="bg-rose-50 rounded-sm p-3">
            <p className="font-heading text-lg font-extrabold text-rose-700">{fmt(summary.total_liens)}</p>
            <p className="text-xs font-semibold text-gray-500">Liens</p>
          </div>
          <div className="bg-amber-50 rounded-sm p-3">
            <p className="font-heading text-lg font-extrabold text-amber-700">{summary.records_pending}</p>
            <p className="text-xs font-semibold text-gray-500">Records pending</p>
          </div>
        </div>
      )}

      <MedicalProviderList providers={providers} onEdit={(p) => { setEditing(p); setDialogOpen(true); }} onDelete={setDeleteTarget} />

      <MedicalProviderFormDialog open={dialogOpen} onOpenChange={setDialogOpen} clientId={clientId} provider={editing} meta={meta} onSaved={load} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-provider-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete provider?</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteTarget?.provider_name}</strong> will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-provider-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
